import {
  CreationType,
  ModelAliasInfo,
  ModelsInfo,
} from 'src/containers/PublishProjectDrawer/types';
import { DataModelSelectItem } from 'src/selectors/dataModelItems';
import { NamedGraph } from 'src/store/state/databases';

export type DanglingNamedGraphIriByModel = Record<string, string>;

/**
 * Returns named graphs that should be deleted on publish.
 *
 * A named graph is only marked for deletion when it is the only reference in any Data Model.
 */
export const getDanglingNamedGraphs = ({
  dataModelItems,
  modelsInfo,
  modelAliasInfo,
  namedGraphs,
}: {
  dataModelItems: DataModelSelectItem[];
  modelsInfo: ModelsInfo;
  modelAliasInfo?: ModelAliasInfo;
  namedGraphs: Record<string, NamedGraph>;
}): DanglingNamedGraphIriByModel => {
  const publishingNamedGraphIrisByModel: Record<string, string[]> = {};

  Object.values(modelsInfo).forEach(
    ({ creationType, modelName, namedGraphIri, shouldPublish }) => {
      if (shouldPublish && creationType === CreationType.REPLACE) {
        publishingNamedGraphIrisByModel[modelName] = [namedGraphIri];
      }
    }
  );

  // handle warnings on Combo Model alias page
  if (modelAliasInfo) {
    const { creationType, modelName, namedGraphIris } = modelAliasInfo;
    if (creationType === CreationType.REPLACE) {
      publishingNamedGraphIrisByModel[modelName] = namedGraphIris;
    }
  }

  const wouldBeNamedGraphIris = [
    ...dataModelItems
      .filter(
        ({ value: modelName }) => !publishingNamedGraphIrisByModel[modelName]
      )
      .map(({ namedGraphIri }) => namedGraphIri),
    ...Object.values(publishingNamedGraphIrisByModel).flatMap(
      (namedGraphIris) => namedGraphIris
    ),
  ];

  const danglingNamedGraphIriByModel: DanglingNamedGraphIriByModel = {};
  dataModelItems.forEach(({ value: modelName, namedGraphIri }) => {
    if (
      !wouldBeNamedGraphIris.includes(namedGraphIri) &&
      // must be a named graph that actually exists.
      // `namedGraphsByModel` can contain graphs that don't exist, and so we shouldn't
      // try to delete those or else we will error on publish.
      namedGraphs[namedGraphIri]
    ) {
      danglingNamedGraphIriByModel[modelName] = namedGraphIri;
    }
  });

  return danglingNamedGraphIriByModel;
};
